import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../store';
import { Input, Button, Select } from '@tadagram/ui';
import { SeedingAccount, WorkerPoolEntry } from '../types';
import { Mail, Lock, Globe, User, Info, Phone } from 'lucide-react';
import { DemographicsForm, DemographicsData } from './DemographicsForm';

interface SeedingAccountFormProps {
  account?: SeedingAccount;
  onClose: () => void;
  onSubmitSuccess?: () => void;
  loading?: boolean;
  setLoading?: (loading: boolean) => void;
  quickFillData?: Record<string, string>;
}

const PLATFORMS = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'threads', label: 'Threads' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'zalo', label: 'Zalo' },
  { value: 'linkedin', label: 'LinkedIn' },
];

// Zalo uses phone number as primary login credential (not email).
// When platform === 'zalo', the Username field becomes a Phone field.
const isZalo = (platform: string) => platform === 'zalo';

// TikTok uses QR-code login — no password, 2FA, or recovery email needed.
// The username field is kept as an optional display label (e.g. @handle or phone)
// so the account can be identified in the admin UI.
const isTikTok = (platform: string) => platform === 'tiktok';

export const SeedingAccountForm: React.FC<SeedingAccountFormProps> = ({ 
  account, 
  onClose,
  onSubmitSuccess,
  loading: externalLoading,
  setLoading: setExternalLoading,
  quickFillData
}) => {
  const { t } = useTranslation('account');
  const { createSeedingAccount, updateSeedingAccount, workerPoolStatus, loadWorkerPoolStatus, brandCharacters, loadBrandCharacters } = useAppStore();

  // Worker selection state (new accounts only)
  const [selectedWorkerUUID, setSelectedWorkerUUID] = useState<string>('');

  // Load pool status and brand characters on mount
  useEffect(() => {
    if (!account) {
      loadWorkerPoolStatus();
    }
    if (brandCharacters.length === 0) {
      loadBrandCharacters();
    }
  }, [account]); // eslint-disable-line react-hooks/exhaustive-deps

  // Prefer pinning to an existing worker with free slots so account creation
  // does not depend on orchestrator auto-allocation when user already has capacity.
  useEffect(() => {
    if (account || !workerPoolStatus) return;

    const workers = workerPoolStatus.my_workers || [];
    if (workers.length === 0) return;

    // Keep current selection if still valid.
    if (selectedWorkerUUID && workers.some((w: WorkerPoolEntry) => w.worker_uuid === selectedWorkerUUID)) {
      return;
    }

    const preferred = workers.find((w: WorkerPoolEntry) => !w.is_full);
    if (preferred) {
      setSelectedWorkerUUID(preferred.worker_uuid);
    }
  }, [account, workerPoolStatus, selectedWorkerUUID]);
  
  const [formData, setFormData] = useState({
    platform: account?.platform || 'facebook',
    fullname: account?.full_name || '',
    username: account?.username || '',
    email: account?.email || '',
    password: '',
    two_fa_secret: '', // NEVER load encrypted 2FA from backend - always start empty
    phone: account?.phone_number || '',
    recovery_email: account?.recovery_email || '',
    brand_character_id: account?.brand_character_id || '', // 🆕 Character ID
  });

  const [fanpageURLs, setFanpageURLs] = useState<string[]>(
    account?.managed_fanpage_urls || []
  );

  // Demographics data (seeding_profile)
  const [demographics, setDemographics] = useState<DemographicsData | undefined>(
    account?.seeding_profile ? {
      seeding_profile: account.seeding_profile
    } : undefined
  );

  const [showDemographics, setShowDemographics] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Apply quick fill data when provided
  useEffect(() => {
    if (quickFillData) {
      setFormData(prev => ({ ...prev, ...quickFillData }));
    }
  }, [quickFillData]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Use external loading if provided, otherwise use internal
  const loading = externalLoading !== undefined ? externalLoading : isSubmitting;
  const setLoading = setExternalLoading || setIsSubmitting;

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.username.trim()) {
      newErrors.username = t('usernameRequired');
    }
    
    // Password only required for new non-TikTok account (TikTok uses QR login)
    if (!account && !formData.password.trim() && !isTikTok(formData.platform)) {
      newErrors.password = t('passwordRequiredNew');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) return;

    setLoading(true);
    try {
      // Map form data to API payload
      const payload: any = {
        platform: formData.platform,
        username: formData.username,
        phone_number: formData.phone,
        recovery_email: formData.recovery_email,
        brand_character_id: formData.brand_character_id || undefined, // 🆕 Include character ID
      };
      
      // Only include password if provided (for update: empty = no change)
      if (formData.password.trim()) {
        payload.password = formData.password;
      }

      // Add optional fields
      if (formData.fullname) {
        payload.full_name = formData.fullname;
      }
      if (formData.email) {
        payload.email = formData.email;
      }
      // Only include 2FA if provided (for update: empty = no change)
      if (formData.two_fa_secret.trim()) {
        payload.two_fa_secret = formData.two_fa_secret;
      }

      // Managed fanpage URLs (Facebook only)
      if (formData.platform === 'facebook') {
        payload.managed_fanpage_urls = fanpageURLs.filter(u => u.trim() !== '');
      }

      // Add demographics (seeding_profile)
      if (demographics?.seeding_profile) {
        payload.seeding_profile = demographics.seeding_profile;
      }

      if (account) {
        await updateSeedingAccount(account.id!, payload);
      } else {
        // Include target worker UUID if user made a selection
        if (selectedWorkerUUID) {
          payload.worker_uuid = selectedWorkerUUID;
        }
        await createSeedingAccount(payload);
      }
      onSubmitSuccess?.();
      onClose();
    } catch (error: any) {
      const errCode: string = error.message || '';
      let submitMsg: string;
      if (errCode === 'PORTAL_WORKER_OFFLINE') {
        submitMsg = 'Worker trong portal riêng tư của bạn hiện đang offline. Vui lòng khởi động portal Worker trước rồi thử lại.';
      } else if (errCode === 'PORTAL_WORKER_MISMATCH') {
        submitMsg = 'Worker đã chọn không thuộc portal riêng tư của bạn. Hãy để trống ô Worker để hệ thống tự phân bổ.';
      } else {
        submitMsg = errCode || t('errorOccurred');
      }
      setErrors({ submit: submitMsg });
    } finally {
      setLoading(false);
    }
  };

  // Show demographics modal
  if (showDemographics) {
    return (
      <DemographicsForm
        initialData={demographics}
        onSave={(data) => {
          setDemographics(data);
          setShowDemographics(false);
        }}
        onCancel={() => setShowDemographics(false)}
      />
    );
  }

  return (
    <form id="seeding-account-form" onSubmit={handleSubmit} className="space-y-6">
      {/* Platform */}
      <Select
        label={t('platform')}
        value={formData.platform}
        onChange={(e) => handleChange('platform', e.target.value)}
        disabled={!!account}
        options={PLATFORMS}
        helperText={account ? t('cannotChangePlatform') : undefined}
      />

      {/* Full Name (Optional) */}
      <div>
        <label className="block text-sm font-medium text-tg-text mb-2">
          {t('fullNameOptional')}
        </label>
        <Input
          type="text"
          value={formData.fullname}
          onChange={(e) => handleChange('fullname', e.target.value)}
          placeholder={t('fullNamePlaceholder')}
          icon={<User size={20} />}
        />
        <p className="text-xs text-tg-hint mt-1">
          {t('fullNameHelper')}
        </p>
      </div>

      {/* Username — Zalo: phone number as login; TikTok: display label only; others: email/ID */}
      <div>
        <label className="block text-sm font-medium text-tg-text mb-2">
          {isZalo(formData.platform)
            ? t('zaloPhoneLabel')
            : isTikTok(formData.platform)
            ? 'TikTok Username / Số điện thoại *'
            : t('usernameEmail')} *
        </label>
        <Input
          type={isZalo(formData.platform) ? 'tel' : 'text'}
          value={formData.username}
          onChange={(e) => handleChange('username', e.target.value)}
          placeholder={
            isZalo(formData.platform)
              ? t('zaloPhonePlaceholder')
              : isTikTok(formData.platform)
              ? '@tiktok_username hoặc +84...'
              : t('usernameHelper')
          }
          icon={isZalo(formData.platform) ? <Phone size={20} /> : <Mail size={20} />}
          error={errors.username}
          required
        />
        <p className="text-xs text-tg-hint mt-1">
          {isZalo(formData.platform)
            ? t('zaloPhoneHelper')
            : isTikTok(formData.platform)
            ? 'Dùng để nhận diện tài khoản trong hệ thống. Không dùng để đăng nhập.'
            : t('usernameHelper')}
        </p>
      </div>

      {/* TikTok QR login notice — replaces the password block */}
      {isTikTok(formData.platform) ? (
        <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-700">
          <strong>🔑 Đăng nhập bằng QR code</strong>
          <p className="mt-1">
            TikTok không cần mật khẩu. Sau khi thêm tài khoản, hệ thống sẽ hiển thị mã QR —
            bạn chỉ cần quét bằng app TikTok trên điện thoại để xác nhận đăng nhập.
          </p>
        </div>
      ) : (
        /* Password — required for Facebook / Zalo / Instagram */
        <div>
          <label className="block text-sm font-medium text-tg-text mb-2">
            {t('password')} {!account && '*'}
          </label>
          <Input
            type="password"
            value={formData.password}
            onChange={(e) => handleChange('password', e.target.value)}
            placeholder={account ? t('leaveEmptyKeepCurrent') : t('passwordPlaceholder')}
            icon={<Lock size={20} />}
            error={errors.password}
            required={!account}
          />
          {account && (
            <p className="text-xs text-tg-hint mt-1">
              {t('passwordChangeHint')}
            </p>
          )}
        </div>
      )}

      {/* 2FA Secret (Optional) — not applicable for Zalo (QR/OTP) or TikTok (QR login) */}
      {!isZalo(formData.platform) && !isTikTok(formData.platform) && (
      <div>
        <label className="block text-sm font-medium text-tg-text mb-2">
          {t('twoFaCode')}
        </label>
        <Input
          type="password"
          value={formData.two_fa_secret}
          onChange={(e) => handleChange('two_fa_secret', e.target.value)}
          placeholder={account ? t('twoFaKeepCurrent') : t('twoFaPlaceholder')}
          icon={<Lock size={20} />}
        />
        <p className="text-xs text-tg-hint mt-1">
          {account 
            ? t('twoFaEditHelper') 
            : t('twoFaNewHelper')}
        </p>
      </div>
      )}

      {/* Email (Optional backup) — not applicable for Zalo or TikTok */}
      {!isZalo(formData.platform) && !isTikTok(formData.platform) && (
      <div>
        <label className="block text-sm font-medium text-tg-text mb-2">
          {t('backupEmail')}
        </label>
        <Input
          type="email"
          value={formData.email}
          onChange={(e) => handleChange('email', e.target.value)}
          placeholder={t('backupEmailPlaceholder')}
          icon={<Mail size={20} />}
        />
        <p className="text-xs text-tg-hint mt-1">
          {t('backupEmailHelper')}
        </p>
      </div>
      )}

      {/* Phone (Optional) — hidden for Zalo because phone IS the username above */}
      {/* For TikTok, phone is shown as an optional reference field */}
      {!isZalo(formData.platform) && (
        <div>
          <label className="block text-sm font-medium text-tg-text mb-2">
            {t('phoneOptional')}
          </label>
          <Input
            type="tel"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            placeholder={t('phonePlaceholder')}
            icon={<Globe size={20} />}
          />
          <p className="text-xs text-tg-hint mt-1">
            {t('phoneHelper')}
          </p>
        </div>
      )}

      {/* Recovery Email (Optional) — not applicable for Zalo or TikTok */}
      {!isZalo(formData.platform) && !isTikTok(formData.platform) && (
      <div>
        <label className="block text-sm font-medium text-tg-text mb-2">
          {t('recoveryEmailLabel')}
        </label>
        <Input
          type="email"
          value={formData.recovery_email}
          onChange={(e) => handleChange('recovery_email', e.target.value)}
          placeholder="recovery@gmail.com"
          icon={<Mail size={20} />}
        />
      </div>
      )}

      {/* Managed Fanpage URLs (Facebook only) */}
      {formData.platform === 'facebook' && (
        <div>
          <label className="block text-sm font-medium text-tg-text mb-2">
            Fanpage đang quản lý
          </label>
          <div className="space-y-2">
            {fanpageURLs.map((url, idx) => (
              <div key={idx} className="flex gap-2 items-center">
                <input
                  type="url"
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={url}
                  onChange={(e) => {
                    const updated = [...fanpageURLs];
                    updated[idx] = e.target.value;
                    setFanpageURLs(updated);
                  }}
                  placeholder="https://www.facebook.com/your.fanpage"
                />
                <button
                  type="button"
                  onClick={() => setFanpageURLs(fanpageURLs.filter((_, i) => i !== idx))}
                  className="text-red-500 hover:text-red-700 px-2 text-lg font-bold"
                  title="Xóa"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setFanpageURLs([...fanpageURLs, ''])}
            className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            + Thêm Fanpage
          </button>
          <p className="text-xs text-tg-hint mt-1">
            URL trang fanpage mà tài khoản này quản lý. Dùng để worker chuyển vai khi thực thi workflow.
          </p>
        </div>
      )}

      {/* Worker Selection (new accounts only) */}
      {!account && workerPoolStatus && workerPoolStatus.my_workers.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-tg-text mb-1">
            {t('workerSelection', 'Chọn worker')}
          </label>
          <Select
            value={selectedWorkerUUID}
            onChange={(e) => setSelectedWorkerUUID(e.target.value)}
            options={[
              { value: '', label: t('workerAutoAllocate', 'Tự động phân bổ (khuyến nghị)') },
              ...workerPoolStatus.my_workers.map((w: WorkerPoolEntry) => ({
                value: w.worker_uuid,
                label: `${w.worker_uuid.slice(-8)} — ${w.account_count}/${w.account_capacity} ${t('accounts', 'tài khoản')}${
                  w.is_full ? ` (${t('workerFull', 'đầy')})` : ''
                }`,
                disabled: w.is_full,
              }))
            ]}
          />
          <p className="text-xs text-tg-hint mt-1">
            {t('workerSelectionHint', 'Để trống để hệ thống tự chọn worker tối ưu.')}
          </p>
        </div>
      )}

      {/* Brand Character (Optional) */}
      <div className="border-t pt-4">
        <Select
          label="Nhân vật (Brand Character)"
          value={formData.brand_character_id}
          onChange={(e) => handleChange('brand_character_id', e.target.value)}
          options={[
            { value: '', label: '-- Không gán nhân vật --' },
            ...brandCharacters.map(char => ({ value: char.id, label: char.name }))
          ]}
          helperText="Tài khoản này sẽ đóng vai nhân vật được chọn. Nếu có, AI sẽ ưu tiên tính cách của nhân vật này thay vì nhân khẩu học bên dưới."
        />
      </div>

      {/* Demographics Section */}
      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-tg-text">
            {t('demographicsInfo')}
          </label>
          {demographics && (
            <span className="text-xs text-green-600 flex items-center gap-1">
              <Info size={14} />
              {t('configured')}
            </span>
          )}
        </div>
        <Button
          type="button"
          variant="secondary"
          onClick={() => setShowDemographics(true)}
          className="w-full"
        >
          <User size={18} className="mr-2" />
          {demographics ? t('editDemographics') : t('addDemographics')}
        </Button>
        <p className="text-xs text-tg-hint mt-2">
          <strong className="text-orange-600">{t('demographicsRequirement')}</strong>
        </p>
      </div>

      {/* Error Message */}
      {errors.submit && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {errors.submit}
        </div>
      )}

      {/* Info */}
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-xs text-blue-700">
          💡 <strong>{t('noteLabel')}</strong> {t('workerAssignmentNote')}
        </p>
      </div>
    </form>
  );
};
